/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CategoryBreadcrumbsSkeleton } from '@/components/category-breadcrumbs/skeleton';
import { ProductGrid } from '@/components/product-grid';
import { Skeleton } from '@/components/ui/skeleton';

interface CategoryPageSkeletonProps {
    filtersOpen?: boolean;
    productCount?: number;
}

export default function CategoryPageSkeleton({ filtersOpen = false, productCount = 8 }: CategoryPageSkeletonProps) {
    return (
        <div data-testid="category-page-loading-state" aria-busy>
            <div className="section-container mt-9 px-0 lg:px-6">
                <div className="mb-2 relative px-6 lg:px-0">
                    <CategoryBreadcrumbsSkeleton />
                </div>

                <div className="mb-2 flex flex-col gap-2 px-6 lg:px-0">
                    <Skeleton className="h-7 w-48" />
                    <div className="flex justify-between">
                        <Skeleton className="h-5 w-20" />
                        <Skeleton className="h-5 w-24" />
                    </div>
                </div>

                <Skeleton className="mb-4 h-10 w-full" />

                <div className="flex min-w-0 gap-4">
                    {filtersOpen && (
                        <div className="hidden w-64 flex-shrink-0 space-y-4 border-t border-gray-200 lg:block">
                            {Array.from({ length: 6 }, (_, index) => (
                                <Skeleton key={`refinement-skeleton-${index}`} className="h-10 w-full" />
                            ))}
                        </div>
                    )}

                    <div className="flex-grow">
                        <Skeleton className="mb-8 h-10 w-full" />
                        <ProductGrid
                            skeletonCount={productCount}
                            hasRefinementsPanel={filtersOpen}
                            isLoading
                            imageContext="plp"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
